import { useEffect, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { api, type ReviewBatchStatus } from "../../api/client";

// ── Steer to Safety ──────────────────────────────────────────────────────────
// The Review pane's flagship action: review & fix EVERY open PR to 5/5, one
// at a time, with the user's own agent. The button is the only place the
// accent color lives in this pane — an "engage" instrument bar with a light
// sweep, a glow on hover, and a readout tooltip explaining the feature.

export function SteerToSafetyButton({
  count,
  running,
  disabled,
  onClick,
}: {
  // open-PR count shown in the scope chip (null while unknown)
  count: number | null;
  running: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="steer-cta"
      data-state={running ? "running" : undefined}
      disabled={disabled || running}
      onClick={onClick}
      data-testid="steer-to-safety"
    >
      <span className="steer-cta__sweep" aria-hidden />
      <ShieldCheck size={14} className="steer-cta__glyph" aria-hidden />
      <span className="steer-cta__label">{running ? "Steering…" : "Steer to Safety"}</span>
      {count != null && count > 0 && !running ? (
        <span className="steer-cta__count" data-testid="steer-count">
          {count}
        </span>
      ) : null}
      <span className="steer-cta__tip" role="tooltip" data-testid="steer-tip">
        Reviews every open PR to a 5/5 score — your agent fixes, pushes and comments, one PR at a
        time.
      </span>
    </button>
  );
}

const POLL_MS = 2500;

// Compact batch readout: one row per PR with its live state and outcome.
export function ReviewBatchPanel({
  batchId,
  onSettled,
  pollMs = POLL_MS,
}: {
  batchId: string;
  onSettled?: () => void;
  pollMs?: number;
}) {
  const [status, setStatus] = useState<ReviewBatchStatus | null>(null);
  const [gone, setGone] = useState(false);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    setStatus(null);
    setGone(false);

    async function poll() {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const s = await api.getReviewBatchStatus(batchId);
        if (cancelled) return;
        setStatus(s);
        if (s.state === "done") {
          onSettledRef.current?.();
          return;
        }
      } catch (err) {
        if (cancelled) return;
        if ((err as { status?: number })?.status === 404) {
          setGone(true);
          onSettledRef.current?.();
          return;
        }
        // transient — keep polling
      } finally {
        inFlight = false;
      }
      if (!cancelled) timer = setTimeout(poll, pollMs);
    }
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [batchId, pollMs]);

  if (gone) return <p className="review-run-note">Batch finished — live status expired.</p>;
  if (!status) return <p className="review-run-note">Starting the batch…</p>;

  const doneCount = status.items.filter((i) =>
    ["done", "error", "skipped"].includes(i.state)
  ).length;

  return (
    <div className="steer-batch" data-testid="steer-batch">
      <p className="review-run-title">
        {status.state === "done"
          ? `Steered ${status.items.length} PR${status.items.length === 1 ? "" : "s"}`
          : `Steering ${doneCount + 1} of ${status.items.length}…`}
      </p>
      {status.error ? <p className="review-run-error">{status.error}</p> : null}
      <ul className="steer-batch__list">
        {status.items.map((i) => (
          <li
            key={`${i.repo}#${i.prNumber}`}
            className="steer-batch__item"
            data-state={i.state}
            data-testid={`steer-item-${i.prNumber}`}
          >
            <span className="steer-batch__pr">#{i.prNumber}</span>
            <span className="steer-batch__title">{i.title || i.repo}</span>
            <span className="steer-batch__state">
              {i.state === "done" && i.rubricFinal
                ? `${i.rubricFinal.overall} / 5`
                : i.state === "running"
                  ? "reviewing…"
                  : i.state}
            </span>
            {i.commentUrl ? (
              <a className="steer-batch__link" href={i.commentUrl} target="_blank" rel="noreferrer">
                comment
              </a>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
